import { Injectable, signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { Observable } from "rxjs";
import { filter } from "rxjs/operators";
import { IBucketItem } from "../models/bucket-item.model";
import { IUserInformation } from "../../../shared/user-information-overlay/user-information";

@Injectable({
  providedIn: "root",
})
export class DataService {
  // BehaviorSubject-backed signals (emit initial value on subscribe)
  private readonly _transferData = signal<Partial<IBucketItem>[]>([]);
  private readonly _reflectCheckBoxChanges = signal<Partial<IBucketItem>[]>([]);
  private readonly _bucketItemsData = signal<Partial<IBucketItem>[]>([]);
  private readonly _dirName = signal<string>("");
  private readonly _sideNavChanged = signal<string>("");
  private readonly _userInformation = signal<IUserInformation>({} as IUserInformation);
  private readonly _isAutoCompleteActive = signal<boolean>(false);
  private readonly _searchBarStatus = signal<number>(1);
  private readonly _permissionList = signal<string[]>([]);

  // Subject/ReplaySubject-backed signals (null = not yet emitted, filtered out)
  private readonly _bucketType = signal<string | null>(null);
  private readonly _clearSearchFlag = signal<boolean | null>(null);
  private readonly _pageNo = signal<number | null>(null);
  private readonly _authorizedToAutomatedTransfer = signal<boolean | null>(null);
  private readonly _authorizedToMaliciousFiles = signal<boolean | null>(null);
  private readonly _justificationPermission = signal<string | null>(null);
  private readonly _packageFolderIds = signal<string[] | null>(null);

  // Observable bridges — field initializers run in injection context
  private readonly bucketDataObs = toObservable(this._transferData);
  private readonly bucketDataObsForCheckBoxChanges = toObservable(this._reflectCheckBoxChanges);
  private readonly bucketDataItems = toObservable(this._bucketItemsData);
  private readonly directoryName = toObservable(this._dirName);
  private readonly sideNavChange = toObservable(this._sideNavChanged);
  private readonly userInformationChanged = toObservable(this._userInformation);
  private readonly autoCompleteActiveStatus = toObservable(this._isAutoCompleteActive);
  private readonly searchBarStatusValue = toObservable(this._searchBarStatus);
  private readonly permissions = toObservable(this._permissionList);
  private readonly bucketTypeObs = toObservable(this._bucketType).pipe(
    filter((v): v is string => v !== null)
  );
  private readonly search = toObservable(this._clearSearchFlag).pipe(
    filter((v): v is boolean => v !== null)
  );
  private readonly pageNoScroll = toObservable(this._pageNo).pipe(
    filter((v): v is number => v !== null)
  );
  private readonly isAuthorizedToAutomatedTransferScreen = toObservable(this._authorizedToAutomatedTransfer).pipe(
    filter((v): v is boolean => v !== null)
  );
  private readonly isAuthorizedToMaliciousFilesScreen = toObservable(this._authorizedToMaliciousFiles).pipe(
    filter((v): v is boolean => v !== null)
  );
  private readonly justificationPermissionName = toObservable(this._justificationPermission).pipe(
    filter((v): v is string => v !== null)
  );
  private readonly packageFolderId$ = toObservable(this._packageFolderIds).pipe(
    filter((v): v is string[] => v !== null)
  );

  public getAllSelectedData(): Observable<Partial<IBucketItem>[]> {
    return this.bucketDataObs;
  }

  public getPackageFolders(): Observable<string[]> {
    return this.packageFolderId$;
  }

  public setPackageFolders(data: string[]): void {
    this._packageFolderIds.set(data);
  }

  public setData(data: Partial<IBucketItem>[]) {
    this._transferData.set(data);
  }

  public setDataForCheckBox(checkboxdata: Partial<IBucketItem>[]) {
    this._reflectCheckBoxChanges.set(checkboxdata);
  }

  public getCheckBoxTransferData(): Observable<Partial<IBucketItem>[]> {
    return this.bucketDataObsForCheckBoxChanges;
  }

  public setBucketType(type: string) {
    this._bucketType.set(type);
  }

  public getBucketType(): Observable<string> {
    return this.bucketTypeObs;
  }

  public setDataForBucketItems(displayedBucketItems: Partial<IBucketItem>[]) {
    this._bucketItemsData.set(displayedBucketItems);
  }

  public getDataForBucketItems(): Observable<Partial<IBucketItem>[]> {
    return this.bucketDataItems;
  }

  public setDirName(dirName: string) {
    this._dirName.set(dirName);
  }

  public getDirName(): Observable<string> {
    return this.directoryName;
  }

  public setAutoCompleteActiveStatus(status: boolean) {
    this._isAutoCompleteActive.set(status);
  }

  public getAutoCompleteActiveStatus(): Observable<boolean> {
    return this.autoCompleteActiveStatus;
  }

  public setSearchBarStatus(status: number) {
    this._searchBarStatus.set(status);
  }

  public getSearchBarStatus(): Observable<number> {
    return this.searchBarStatusValue;
  }

  public setPermissionList(permissions: string[]) {
    this._permissionList.set(permissions || []);
  }

  public getPermissionList(): Observable<string[]> {
    return this.permissions;
  }

  public setSideNav(sideNav: string) {
    this._sideNavChanged.set(sideNav);
  }

  public getSideNav(): Observable<string> {
    return this.sideNavChange;
  }

  public setClearSearch(value: boolean) {
    this._clearSearchFlag.set(value);
  }

  public getClearSearch(): Observable<boolean> {
    return this.search;
  }

  public setPageNo(value: number) {
    this._pageNo.set(value);
  }

  public getPageNo(): Observable<number> {
    return this.pageNoScroll;
  }

  public setAuthorizedToAutomatedTransferScreen(value: boolean) {
    this._authorizedToAutomatedTransfer.set(value);
  }

  public getAuthorizedToAutomatedTransferScreen(): Observable<boolean> {
    return this.isAuthorizedToAutomatedTransferScreen;
  }

  public setAuthorizedToMaliciousFilesScreen(value: boolean) {
    this._authorizedToMaliciousFiles.set(value);
  }

  public getAuthorizedToMaliciousFilesScreen(): Observable<boolean> {
    return this.isAuthorizedToMaliciousFilesScreen;
  }

  public setJustificationPermission(value: string) {
    this._justificationPermission.set(value);
  }

  public getJustificationPermission(): Observable<string> {
    return this.justificationPermissionName;
  }

  public setUserInformation(userInformation: IUserInformation) {
    this._userInformation.set(userInformation);
  }

  public getUserInformation(): Observable<IUserInformation> {
    return this.userInformationChanged;
  }
}
